import { useCallback, useEffect, useRef, useState } from 'react';
import { setChatDrawerState } from './chatDrawerState.ts';
import type { ChatPageNudge } from './chatPageOffer.ts';
import type { PageOpening } from './chatPageOpening.ts';

type NudgeOpening = Extract<PageOpening, { kind: 'nudge' }>;

export interface DrawerNudge {
  /** The recommendation to draw, or null. */
  nudge: ChatPageNudge | null;
  /** It was refused or taken up: take it off this drawer. */
  settle: () => void;
}

/**
 * The recommendation a page puts in the drawer. Only in a drawer that is blank
 * and stays blank: it is what stands there while there is nothing to read, and
 * the moment there is something it has no business being on screen.
 */
export function useDrawerNudge(opening: PageOpening | null, blank: boolean): DrawerNudge {
  /*
   * The nudges taken off this drawer by hand or by being taken up. The page
   * goes on offering the same one — a refusal is counted, not obeyed until the
   * third — so this is what makes the ✕ mean anything on the screen it was
   * clicked on.
   */
  const [settled, setSettled] = useState<ReadonlySet<string>>(() => new Set());

  const nudge =
    opening?.kind === 'nudge' && blank && !settled.has(opening.key) ? opening : null;
  const nudgeKey = nudge?.key ?? null;

  const settle = useCallback(() => {
    if (nudgeKey !== null) {
      setSettled((current) => new Set(current).add(nudgeKey));
    }
  }, [nudgeKey]);

  useOpenForFirstTime(nudge);

  return { nudge: nudge?.nudge ?? null, settle };
}

/**
 * A nudge nobody has ever been shown gets the drawer opened for it, once.
 *
 * Whether it is the first time is the server's answer: it stamps the showing
 * as it hands the nudge over, so the same nudge on the next page comes back
 * saying it is no longer new. This opens the drawer, and never closes it.
 *
 * Held by the nudge's own key rather than by "has a first-time nudge": the
 * nudge can arrive any number of renders after this drawer mounted, and the
 * nudge that arrives is what has to open the drawer. Keyed, a second nudge
 * later in the same visit still opens it, and collapsing stays collapsed.
 */
function useOpenForFirstTime(nudge: NudgeOpening | null): void {
  const firstTimeNudgeKey = nudge?.nudge.firstTime === true ? nudge.key : null;
  const openedFor = useRef<string | null>(null);

  useEffect(() => {
    if (firstTimeNudgeKey === null || firstTimeNudgeKey === openedFor.current) {
      return;
    }

    /* Stamped first: it has had its once, whatever the drawer was already doing. */
    openedFor.current = firstTimeNudgeKey;
    setChatDrawerState('open');
  }, [firstTimeNudgeKey]);
}
